import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Flex, Grid, HStack, Icon, Input, SimpleGrid, Spinner, Text, Tooltip } from '@chakra-ui/react';
import { RiCheckboxCircleLine, RiErrorWarningLine, RiArrowRightLine, RiSmartphoneLine, RiShieldCheckLine } from 'react-icons/ri';

import useAppToast from 'components/toast';
import { Banner, SectionCard, StatCard } from '../_ui/components';
import { useCampaignUI } from '../_ui/ui';
import {
	usePreviewCampaignMutation,
	useSubmitCampaignMutation,
	useListMetaWhatsappTemplatesQuery,
} from 'api/metaWhatsappApi';
import TemplateBubble from 'views/admin/meta-whatsapp/_components/TemplateBubble';
import campaignError from '../campaignErrors';
import { campaignFromResponse } from '../_lib/campaignState';
import { previewOf } from './StepMessage';

/**
 * What this will actually do, before it does it.
 *
 * ★ THE FUNNEL COMES FROM THE SERVER, RUN THROUGH THE SAME CODE THE PLANNER
 * USES. So the number approved here is the number that gets built — not an
 * estimate this screen calculated and not a count from a different query. It
 * writes nothing: somebody must be able to try five filters without leaving
 * five half-built campaigns behind.
 *
 * ★ EVERY BLOCKER POINTS AT ITS OWN STEP. "This cannot be sent yet" followed
 * by four sentences run together told a person what was wrong and left them to
 * work out where. Each problem is now a row with a Fix button that opens the
 * step that owns it, which turns a wall of text into a checklist that empties.
 *
 * ★ THE AUDIENCE IS DRAWN, NOT LISTED. One bar, split into who will be
 * messaged and every reason somebody will not. "33 considered, 0 will be
 * messaged" with the reasons underneath is what makes a data problem obvious
 * before money is spent — a list of eight numbers is what let it hide.
 */

/** Why contacts fell out, in the order they are filtered, with the tone each deserves. */
const EXCLUSIONS = [
	['badFormat', 'Number could not be read', 'danger'],
	['whatsappInvalid', 'Known not to be on WhatsApp', 'warning'],
	['noConsent', 'No consent on record', 'info'],
	['doNotMarket', 'Marked do-not-market', 'textMuted'],
	['suppressed', 'On the do-not-message list', 'textMuted'],
	['excluded', 'Excluded by hand', 'textMuted'],
	['frequencyCapped', 'Messaged too recently', 'iconMuted'],
	['alreadyInCampaign', 'Already in this campaign', 'iconMuted'],
];

/**
 * Which step owns a problem code, by prefix.
 *
 * The codes are the server's (`validateForLaunch`), and they arrive as
 * `CODE`, `CODE:template` or `CODE@variant[.step]`. Anything unrecognised gets
 * its sentence and no button — a wrong button is worse than none.
 */
const STEP_FOR = [
	[/^ATTESTATION_/, 0],
	[/^(NO_VARIANT|TEMPLATE_|DRIP_|TOO_MANY_DRIP|MISSING_|PARAM|VARIABLE|RENDER)/, 1],
	[/^(SCHEDULE_|WINDOW_|TIMEZONE_|ALL_WEEKDAYS)/, 2],
];
const STEP_LABEL = ['Audience', 'Message', 'Senders & schedule'];

/** `TEMPLATE_NOT_FOUND:hello_world@a` → { code, where: 'variant A' }. */
function describeProblem(raw) {
	const text = String(raw ?? '');
	const code = text.split(':')[0].split('@')[0];
	const at = text.includes('@') ? text.split('@')[1] : '';
	const [variant, step] = at.split('.');
	const where = variant ? `variant ${variant.toUpperCase()}${step ? `, follow-up ${Number(step) + 1}` : ''}` : '';
	const target = STEP_FOR.find(([re]) => re.test(code))?.[1] ?? null;
	return { code, sentence: campaignError(code), where, target };
}

const num = (v) => Number(v ?? 0).toLocaleString();

/** One bar for the whole audience: who is in, and why everyone else is out. */
function AudienceBar({ sendable, excluded }) {
	const ui = useCampaignUI();
	const parts = [
		{ key: 'sendable', label: 'Will be messaged', count: sendable, color: ui.accent },
		...EXCLUSIONS.map(([key, label, tone]) => ({ key, label, count: Number(excluded?.[key] ?? 0), color: ui[tone] })),
	].filter((p) => p.count > 0);
	const total = parts.reduce((a, p) => a + p.count, 0);
	if (!total) {
		return (
			<Text fontSize='sm' color={ui.textMuted}>
				Nobody matched. Widen the audience on step 1.
			</Text>
		);
	}
	return (
		<Box>
			<Flex h='14px' borderRadius='full' overflow='hidden' bg={ui.surfaceSubtle} role='img' aria-label={`${num(sendable)} of ${num(total)} will be messaged`}>
				{parts.map((p) => (
					<Tooltip key={p.key} hasArrow label={`${p.label}: ${num(p.count)}`}>
						<Box w={`${(p.count / total) * 100}%`} minW='3px' bg={p.color} transition='width 300ms ease' />
					</Tooltip>
				))}
			</Flex>
			<Flex mt={3} gap={{ base: 2, md: 4 }} wrap='wrap'>
				{parts.map((p) => (
					<HStack key={p.key} spacing={1.5} fontSize='xs'>
						<Box boxSize='9px' borderRadius='full' bg={p.color} flexShrink={0} />
						<Text color={p.key === 'sendable' ? ui.textPrimary : ui.textSecondary} fontWeight={p.key === 'sendable' ? 'semibold' : 'normal'}>
							{p.label}
						</Text>
						<Text color={ui.textMuted}>{num(p.count)}</Text>
					</HStack>
				))}
			</Flex>
		</Box>
	);
}

export default function StepReview({ campaign, draft, patch, goTo, flush }) {
	const ui = useCampaignUI();
	const navigate = useNavigate();
	const notify = useAppToast();

	const [preview, { isLoading }] = usePreviewCampaignMutation();
	const [submit, { isLoading: isSubmitting }] = useSubmitCampaignMutation();
	const { data: templates } = useListMetaWhatsappTemplatesQuery({ status: 'APPROVED' });
	const [result, setResult] = useState(null);
	const [previewError, setPreviewError] = useState(null);
	const [retry, setRetry] = useState(0);
	const [preparingSubmit, setPreparingSubmit] = useState(false);
	const submitLock = useRef(false);

	// Run once when the step opens — it is expensive, so never on a rerender.
	useEffect(() => {
		let cancelled = false;
		(async () => {
			try {
				setPreviewError(null);
				await flush();
				const data = await preview(campaign._id).unwrap();
				if (!cancelled) setResult(data);
			} catch (err) {
				if (!cancelled) setPreviewError(err);
			}
		})();
		return () => {
			cancelled = true;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [campaign._id, retry, flush, preview]);

	const phone = useMemo(() => {
		const first = draft.variants?.[0];
		return first && Array.isArray(templates) ? previewOf(first, templates) : null;
	}, [draft.variants, templates]);

	const onSubmit = async () => {
		if (submitLock.current) return;
		submitLock.current = true; setPreparingSubmit(true);
		try {
			await flush();
			const updated = await submit(campaign._id).unwrap();
			const campaignState = campaignFromResponse(updated)?.state;
			notify.success(
				campaignState === 'PENDING_APPROVAL' ? 'Sent for approval' : 'Ready to launch',
				campaignState === 'PENDING_APPROVAL'
					? 'A colleague needs to approve it before it goes out.'
					: 'Open the campaign and choose Launch campaign once when you are ready.',
			);
			navigate(`/meta-campaigns/${campaign._id}`);
		} catch (err) {
			notify.error('Could not submit', campaignError(err));
		} finally { submitLock.current = false; setPreparingSubmit(false); }
	};

	if (previewError) {
		return <Box><Banner status='error' title='We could not prepare the review' description={campaignError(previewError)} /><Button mt={4} onClick={() => setRetry((value) => value + 1)}>Retry review</Button></Box>;
	}

	if (isLoading || !result) {
		return (
			<Flex justify='center' py={16} direction='column' align='center' gap={3}>
				<Spinner color={ui.accentInk} />
				<Text fontSize='sm' color={ui.textMuted}>
					Working out who this reaches…
				</Text>
			</Flex>
		);
	}

	const counters = result.counters ?? {};
	const excluded = result.excluded ?? {};
	const cost = result.cost ?? {};
	const launch = result.launch ?? {};
	const problems = (launch.problems ?? []).map(describeProblem);
	// Approval policy is decided by the server at submission; do not guess it from category.
	const nobody = !counters.sendable;
	const blocked = !launch.ok || nobody;

	return (
		<Grid templateColumns={{ base: '1fr', xl: 'minmax(0, 1fr) 340px' }} gap={{ base: 5, xl: 6 }} alignItems='start'>
			<Box minW={0}>
				{/* The name is the thing the list, the approver and the audit will
				    show — worth its own moment rather than a stray input. */}
				<SectionCard title='Name this campaign' subtitle='This is what colleagues will see in the list and in the approval.' mb={5}>
					<Input
						size='md'
						maxW='520px'
						value={draft.name}
						onChange={(e) => patch({ name: e.target.value })}
						placeholder='e.g. Marina launch — October viewings'
						bg={ui.inputBg}
						borderColor={ui.border}
						color={ui.textPrimary}
						fontWeight='semibold'
					/>
				</SectionCard>

				<SectionCard
					title={problems.length ? `${problems.length} thing${problems.length === 1 ? '' : 's'} to fix first` : nobody ? 'No eligible recipients' : 'Ready to submit'}
					subtitle={problems.length ? 'Each one opens the step that fixes it.' : nobody ? 'Adjust the audience before submitting.' : 'Review these results before submitting.'}
					mb={5}
				>
					{problems.length ? (
						problems.map((p, i) => (
							<Flex
								key={`${p.code}-${i}`}
								align='center'
								gap={3}
								py={2.5}
								borderTopWidth={i ? '1px' : 0}
								borderColor={ui.borderSubtle}
							>
								<Icon as={RiErrorWarningLine} color={ui.danger} boxSize={5} flexShrink={0} />
								<Box flex='1' minW={0}>
									<Text fontSize='sm' color={ui.textPrimary}>
										{p.sentence}
									</Text>
									{p.where && (
										<Text fontSize='xs' color={ui.textMuted}>
											{p.where}
										</Text>
									)}
								</Box>
								{p.target !== null && typeof goTo === 'function' && (
									<Button
										size='xs'
										variant='neutralOutline'
										borderColor={ui.border}
										color={ui.textPrimary}
										rightIcon={<RiArrowRightLine />}
										onClick={() => goTo(p.target)}
										flexShrink={0}
									>
										Fix in {STEP_LABEL[p.target]}
									</Button>
								)}
							</Flex>
						))
					) : (
						<HStack spacing={3}>
							<Icon as={RiCheckboxCircleLine} color={ui.success} boxSize={6} />
							<Text fontSize='sm' color={ui.textSecondary}>
								Audience, message and schedule all check out.
							</Text>
						</HStack>
					)}
				</SectionCard>

				<SimpleGrid columns={{ base: 2, lg: 4 }} spacing={4} mb={5}>
					<StatCard label='Leads considered' value={num(counters.deduped)} />
					<StatCard label='Will be messaged' value={num(counters.sendable)} help={nobody ? 'Nobody — see below' : undefined} />
					<StatCard label='Days needed' value={result.minDays ?? '—'} help={result.minDays > 1 ? `~${num(result.dailyPoolCapacity)} a day` : undefined} />
					<StatCard label='Estimated cost' value={cost.rated ? `${cost.amount} ${cost.currency}` : '—'} help={!cost.rated ? 'No rate card set' : undefined} />
				</SimpleGrid>

				<SectionCard title='Who this reaches' subtitle='Every contact the audience matched, and why any of them will not be messaged.' mb={5}>
					<AudienceBar sendable={counters.sendable ?? 0} excluded={excluded} />
					<SimpleGrid columns={{ base: 3 }} spacing={3} mt={5} pt={4} borderTopWidth='1px' borderColor={ui.borderSubtle}>
						<Box>
							<Text fontSize='xs' color={ui.textMuted}>
								Proven on WhatsApp
							</Text>
							<Text fontSize='lg' color={ui.success} fontWeight='semibold'>
								{num(counters.whatsappValid)}
							</Text>
						</Box>
						<Box>
							<Text fontSize='xs' color={ui.textMuted}>
								Not yet known
							</Text>
							<Text fontSize='lg' color={ui.textSecondary} fontWeight='semibold'>
								{num(counters.whatsappUnknown)}
							</Text>
						</Box>
						<Box>
							<Text fontSize='xs' color={ui.textMuted}>
								Time zone guessed
							</Text>
							<Text fontSize='lg' color={result.tzFallbackCount ? ui.warning : ui.textSecondary} fontWeight='semibold'>
								{num(result.tzFallbackCount)}
							</Text>
						</Box>
					</SimpleGrid>
					<Text fontSize='xs' color={ui.textMuted} mt={3}>
						An unknown number is still worth messaging — a failed send costs nothing, and the attempt is what
						makes it known. A guessed time zone means we had nothing to place that person by, so they get the
						campaign's default hour.
					</Text>
				</SectionCard>

				{result.minDays > 1 && (
					<Box mb={5}>
						<Banner
							status='info'
							title={`This will take about ${result.minDays} days`}
							description={`Your numbers can carry roughly ${num(result.dailyPoolCapacity)} messages a day between them. Widen the date range, add a number, or raise the cap to go faster.`}
						/>
					</Box>
				)}

				<Flex
					align='center'
					justify='space-between'
					gap={4}
					wrap='wrap'
					p={4}
					borderRadius='xl'
					borderWidth='1px'
					borderColor={blocked ? ui.border : ui.accent}
					bg={blocked ? ui.surface : ui.accentSubtle}
				>
					<HStack spacing={3} minW={0}>
						<Icon as={RiShieldCheckLine} boxSize={5} color={blocked ? ui.iconMuted : ui.accentInk} />
						<Text fontSize='sm' color={ui.textSecondary}>
							{blocked
								? nobody && !problems.length
									? 'Nobody would be messaged, so there is nothing to submit.'
									: 'Fix the items above to submit.'
								: 'The server will check whether approval is required. Submitting does not launch this campaign.'}
						</Text>
					</HStack>
					<Button
						size='md'
						
						
						
						isDisabled={blocked}
						isLoading={isSubmitting || preparingSubmit}
						onClick={onSubmit}
						flexShrink={0} variant='brand'
					>
						Submit campaign
					</Button>
				</Flex>
			</Box>

			{/* The last look. The same bubble the Message step drew, so what is
			    approved here is what was seen there. */}
			<Box position={{ base: 'static', xl: 'sticky' }} top='88px'>
				<HStack spacing={2} mb={3}>
					<RiSmartphoneLine color={ui.accentInk} />
					<Text fontSize='sm' fontWeight='semibold' color={ui.textPrimary}>
						What they will receive
					</Text>
				</HStack>
				<Box
					borderRadius='2xl'
					borderWidth='1px'
					borderColor={ui.border}
					bg={ui.surfaceSubtle}
					p={{ base: 4, md: 5 }}
					minH='220px'
					backgroundImage={`radial-gradient(${ui.border} 1px, transparent 1px)`}
					backgroundSize='14px 14px'
				>
					{phone ? (
						<TemplateBubble
							header={phone.header}
							headerKind={phone.headerKind}
							body={phone.body}
							footer={phone.footer}
							buttons={phone.buttons}
							stamp={`Variant ${String(draft.variants[0].id).toUpperCase()}${(draft.variants?.length ?? 0) > 1 ? ` of ${draft.variants.length}` : ''}`}
						/>
					) : (
						<Flex direction='column' align='center' justify='center' minH='180px' textAlign='center' px={4}>
							<RiSmartphoneLine size={26} color={ui.iconMuted} />
							<Text fontSize='sm' color={ui.textMuted} mt={3}>
								No template chosen yet.
							</Text>
						</Flex>
					)}
				</Box>
			</Box>
		</Grid>
	);
}
